<script lang="ts" setup>
import { computed, onMounted, ref, watch, onUnmounted } from 'vue';
import { storeToRefs } from 'pinia';
import useMediaStore from '../../stores/media';
import { useDisplay } from 'vuetify';
import useLocale from '../../composables/useLocale';

type Emits = {
  (e: 'seekToTime', time: number): void;
};
const emit = defineEmits<Emits>();

const { xs } = useDisplay();
const { t } = useLocale();
const mediaStore = useMediaStore();
const { currentPlayerTime, audioDuration } = storeToRefs(mediaStore);
const { setAudioScrubbingTime } = mediaStore;

const liveSliderValue = ref(0);
const isScrubbing = ref(false);

const sliderMax = computed(() => {
  return Math.max(0, audioDuration.value);
});

const currentSliderValue = computed(() => {
  return currentPlayerTime.value;
});

watch(
  currentSliderValue,
  (newValue) => {
    // Only update if not actively scrubbing
    if (!isScrubbing.value) {
      liveSliderValue.value = newValue;
    }
  },
  { immediate: true },
);

/**
 * Called when slider is being dragged
 */
const handleUpdate = (val: number) => {
  liveSliderValue.value = val;
  setAudioScrubbingTime(val);
  isScrubbing.value = true;
};

/**
 * Called when slider interaction starts
 */
const handleStart = () => {
  isScrubbing.value = true;
  setAudioScrubbingTime(liveSliderValue.value);
};

/**
 * Called when slider drag/interaction ends
 */
const handleChange = () => {
  const newValue = liveSliderValue.value;
  isScrubbing.value = false;
  setAudioScrubbingTime(undefined);

  emit('seekToTime', newValue);
};

onMounted(() => {
  liveSliderValue.value = currentSliderValue.value;
});

onUnmounted(() => {
  setAudioScrubbingTime(undefined);
});
</script>

<template>
  <div class="progress-wrapper d-flex flex-column justify-center w-100" data-testid="bp-time-based-progress-slider">
    <!-- Slider row with prepend/append inline -->
    <div class="d-flex align-center justify-space-between w-100" :class="{ 'mt-2': xs }">
      <!-- Prepend Slot -->
      <div class="d-flex align-center">
        <slot name="prepend"></slot>
      </div>

      <!-- Main Progress / Slider -->
      <div class="d-flex align-center flex-grow-1 px-2">
        <v-locale-provider class="w-100">
          <v-slider
            :model-value="liveSliderValue"
            class="time-based-progress-slider w-100"
            :class="{ 'is-scrubbing': isScrubbing }"
            :min="0"
            :max="sliderMax"
            hide-details
            step="0.1"
            color="accent-original"
            :aria-label="t('book_player.label_time_seeking')"
            track-size="6"
            thumb-size="24"
            :max-width="440"
            @start="handleStart"
            @end="handleChange"
            @update:model-value="handleUpdate"
          />
        </v-locale-provider>
      </div>

      <!-- Append Slot -->
      <div class="d-flex align-center">
        <slot name="append" />
      </div>
    </div>

    <!-- Bottom Slot row (with offset to align with slider) -->
    <div class="bottom-slot-wrapper">
      <slot name="bottom"></slot>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.progress-wrapper {
  min-height: 48px;
}

.bottom-slot-wrapper {
  width: 100%;
  padding-left: calc(40px + 0.5rem); // Approximate prepend width + padding
  padding-right: calc(40px + 0.5rem); // Approximate append width + padding
}

:deep(.v-slider) {
  cursor: grab;

  &.is-scrubbing {
    cursor: grabbing;
  }
}

:deep(.v-slider-thumb__label) {
  top: -20px !important;
  background-color: transparent !important;
  color: rgb(var(--v-theme-on-brand)) !important;
}

:deep(.v-slider-thumb__surface) {
  z-index: 100 !important;
  position: relative !important;
}

:deep(.v-slider-thumb__label::before) {
  color: transparent;
}

:deep(.v-slider__thumb-container .v-slider__thumb-label) {
  height: 12px !important;
  background-color: transparent !important;
  border-color: transparent !important;

  .thumb-label-text {
    font-weight: bold;
    padding-top: 12px;
  }
}

// Override from node_modules/vuetify/src/components/VSlider/variables.sass
$slider-thumb-focused-size-increase: 30px;
$slider-thumb-size: 20px;

:deep(.v-slider__thumb) {
  width: $slider-thumb-size;
  height: $slider-thumb-size;
  left: calc($slider-thumb-focused-size-increase / 2);
  transition: transform 0.2s ease;

  &:before {
    width: #{$slider-thumb-size + $slider-thumb-focused-size-increase};
    height: #{$slider-thumb-size + $slider-thumb-focused-size-increase};
    left: calc($slider-thumb-focused-size-increase / 2);
    top: calc($slider-thumb-focused-size-increase / 2);
  }
}

:deep(.is-scrubbing .v-slider__thumb) {
  transform: scale(1.2);
}

:deep(.v-slider--disabled .v-slider__thumb) {
  width: #{$slider-thumb-size - 4};
  height: #{$slider-thumb-size - 4};
  left: calc(#{$slider-thumb-size - 4} / 2);
}

// Enhance track visibility during scrubbing
:deep(.is-scrubbing .v-slider__track-container) {
  opacity: 0.9;
}
</style>
